package uz.mint.frontend.api

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.INCLUDE
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import uz.mint.frontend.SiteConfig

/**
 * Shared transport for the authenticated part of the API.
 *
 * Two things every call here needs and [CatalogApi] does not, because the catalogue is
 * anonymous:
 *
 *  - **`credentials: include`.** The session lives in an httpOnly cookie, and `fetch` omits
 *    cookies on cross-origin requests unless told otherwise. In development the storefront is
 *    on :8080 and the API on :8081, so *every* call is cross-origin — without this the customer
 *    is anonymous on every request and the login appears to silently fail.
 *  - **The CSRF header.** The backend hands out an `XSRF-TOKEN` cookie on any GET and expects
 *    it echoed back on state-changing requests. Reading it from `document.cookie` works because
 *    that one cookie is deliberately not httpOnly.
 *
 * Non-2xx is returned rather than thrown: 401, 410 and 429 are all *expected* answers in the
 * OTP flow, each with its own message for the customer, and turning them into exceptions would
 * mean unwrapping them again at every call site.
 */
object ApiClient {

    data class Response(val status: Int, val body: String) {
        val ok: Boolean get() = status in 200..299

        /** The backend's `error` discriminator, when the body carries one. */
        val errorCode: String? get() = runCatching { json<ErrorJson>().error }.getOrNull()

        fun <T> json(): T = JSON.parse(body)
    }

    suspend fun get(path: String): Response = send("GET", path, null)

    suspend fun post(path: String, body: String? = null): Response = send("POST", path, body)

    suspend fun patch(path: String, body: String): Response = send("PATCH", path, body)

    suspend fun delete(path: String): Response = send("DELETE", path, null)

    private suspend fun send(method: String, path: String, body: String?): Response {
        val headers: dynamic = js("({})")
        if (body != null) headers["Content-Type"] = "application/json"
        csrfToken()?.let { headers["X-XSRF-TOKEN"] = it }

        val response = window.fetch(
            "${SiteConfig.apiOrigin}$path",
            RequestInit(
                method = method,
                headers = headers,
                body = body,
                credentials = RequestCredentials.INCLUDE,
            ),
        ).await()

        // 204 has no body; `.text()` yields "" and JSON.parse would choke on it downstream.
        val text = if (response.status.toInt() == 204) "" else response.text().await()
        return Response(response.status.toInt(), text)
    }

    /**
     * Fetches a CSRF token if we do not have one yet.
     *
     * The token cookie is only written when the server renders the token, which any GET does.
     * Calling this before the first POST of a session — a customer whose very first action is
     * "log in" — avoids a 403 on that request alone.
     */
    suspend fun ensureCsrfToken() {
        if (csrfToken() == null) runCatching { get("/api/auth/me") }
    }

    private fun csrfToken(): String? = document.cookie
        .split(';')
        .map(String::trim)
        .firstOrNull { it.startsWith("$CSRF_COOKIE=") }
        ?.substringAfter('=')
        ?.let(::decodeURIComponent)
        ?.takeIf { it.isNotBlank() }

    private const val CSRF_COOKIE = "XSRF-TOKEN"
}

private external interface ErrorJson {
    val error: String?
}

internal external fun encodeURIComponent(value: String): String

private external fun decodeURIComponent(value: String): String
